import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop, strokeDashTypes } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
  mergeAppearance,
  resolveStroke,
} from "./utils";

import StraightLine from "./components/StraightLine";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

// Base das 4 ferramentas de linha reta (TrendLine / Ray / Segment / Line):
// harness de draw, drag/override, wiring do MouseLocationIndicator e
// persistência eram ~300 linhas verbatim por arquivo (auditoria 2026-07-17,
// item 1b). Cada tool vira uma subclasse de config com estáticos:
//   static collectionKey  — nome da prop de coleção ("trends", "rays", …)
//   static EachComponent  — wrapper Each* da tool
//   static evoId          — data-evo-id do MouseLocationIndicator
// e defaultProps próprios (type, coleção vazia).
//
// Backfill de `type` UNIFICADO nos 4 (antes só Ray/Line tinham — validação
// Fable §2.3): drag-complete e criação persistem `type` explícito, e o render
// resolve com fallback — desenhos legados sem `type` saram na primeira edição.
class StraightLineTool extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawLine = this.handleDrawLine.bind(this);
    this.handleDragLine = this.handleDragLine.bind(this);
    this.handleDragLineComplete = this.handleDragLineComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType(
      this.constructor.collectionKey,
    ).bind(this);

    this.state = {};
    this.nodes = [];
  }
  getFallbackType() {
    const { type } = this.props;
    return isDefined(type) ? type : this.constructor.defaultProps.type;
  }
  handleDragLine(index, newXYValue) {
    this.setState({
      override: {
        index,
        ...newXYValue,
      },
    });
  }
  handleDragLineComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const objects = this.props[this.constructor.collectionKey];
      const fallbackType = this.getFallbackType();
      const newObjects = objects.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              type: isDefined(each.type) ? each.type : fallbackType,
              start: [override.x1Value, override.y1Value],
              end: [override.x2Value, override.y2Value],
              selected: true,
            }
          : {
              ...each,
              type: isDefined(each.type) ? each.type : fallbackType,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newObjects, moreProps);
        },
      );
    }
  }
  handleDrawLine(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.start)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          start: current.start,
          end: xyValue,
        },
      });
    }
  }
  handleStart(xyValue, moreProps, e) {
    const { current } = this.state;

    if (isNotDefined(current) || isNotDefined(current.start)) {
      this.mouseMoved = false;

      this.setState(
        {
          current: {
            start: xyValue,
            end: null,
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }
  handleEnd(xyValue, moreProps, e) {
    const { current } = this.state;
    const { appearance } = this.props;
    const objects = this.props[this.constructor.collectionKey];

    if (this.mouseMoved && isDefined(current) && isDefined(current.start)) {
      // Use the current appearance (which includes the drawing color from
      // InteractiveLayer) instead of fetching from registry which would
      // override the selected color
      const currentAppearance = appearance;
      const fallbackType = this.getFallbackType();

      const newObjects = [
        ...objects.map((d) => ({ ...d, selected: false })),
        {
          start: current.start,
          end: xyValue,
          selected: true,
          appearance: currentAppearance,
          type: fallbackType,
        },
      ];
      this.setState(
        {
          current: null,
          [this.constructor.collectionKey]: newObjects,
        },
        () => {
          this.props.onComplete(newObjects, moreProps, e);
        },
      );
    }
  }
  render() {
    const { appearance } = this.props;
    const { enabled, snap, shouldDisableSnap, snapTo } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionstrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { hoverText } = this.props;
    const objects = this.props[this.constructor.collectionKey];
    const { current, override } = this.state;
    const { EachComponent, evoId } = this.constructor;
    const fallbackType = this.getFallbackType();

    const currentStroke = resolveStroke(appearance);
    const tempLine =
      isDefined(current) && isDefined(current.end) ? (
        <StraightLine
          type={fallbackType}
          noHover
          x1Value={current.start[0]}
          y1Value={current.start[1]}
          x2Value={current.end[0]}
          y2Value={current.end[1]}
          stroke={currentStroke.stroke}
          strokeWidth={currentStroke.strokeWidth}
          strokeOpacity={currentStroke.strokeOpacity}
        />
      ) : null;

    return (
      <g>
        {objects.map((each, idx) => {
          const eachAppearance = mergeAppearance(appearance, each.appearance);
          const eachStroke = resolveStroke(eachAppearance);

          const hoverTextWithDefault = {
            ...this.constructor.defaultProps.hoverText,
            ...hoverText,
          };
          const resolvedType = isDefined(each.type) ? each.type : fallbackType;

          return (
            <EachComponent
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              type={resolvedType}
              selected={each.selected}
              x1Value={getValueFromOverride(
                override,
                idx,
                "x1Value",
                each.start[0],
              )}
              y1Value={getValueFromOverride(
                override,
                idx,
                "y1Value",
                each.start[1],
              )}
              x2Value={getValueFromOverride(
                override,
                idx,
                "x2Value",
                each.end[0],
              )}
              y2Value={getValueFromOverride(
                override,
                idx,
                "y2Value",
                each.end[1],
              )}
              stroke={eachStroke.stroke}
              strokeWidth={eachStroke.strokeWidth}
              strokeOpacity={eachStroke.strokeOpacity}
              strokeDasharray={eachStroke.strokeDasharray}
              edgeStroke={eachAppearance.edgeStroke}
              edgeFill={eachAppearance.edgeFill}
              r={eachAppearance.r}
              labelFont={eachAppearance.labelFont}
              hoverText={hoverTextWithDefault}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
              edgeInteractiveCursor="react-stockcharts-move-cursor"
              lineInteractiveCursor="react-stockcharts-move-cursor"
            />
          );
        })}
        {tempLine}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEnd}
          onMouseMove={this.handleDrawLine}
          data-evo-id={evoId}
        />
      </g>
    );
  }
}

// propTypes comuns — cada subclasse acrescenta a prop de coleção e o oneOf de type
export const straightLinePropTypes = {
  snap: PropTypes.bool.isRequired,
  enabled: PropTypes.bool.isRequired,
  snapTo: PropTypes.func,
  shouldDisableSnap: PropTypes.func.isRequired,

  onStart: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionstrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,
  hoverText: PropTypes.object.isRequired,

  appearance: PropTypes.shape({
    // Registry format (primary)
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string, // Legacy support
    ]),
    // Legacy properties (fallback support)
    strokeOpacity: PropTypes.number,
    strokeWidth: PropTypes.number,
    strokeDasharray: PropTypes.oneOf(strokeDashTypes),
    // Edge stroke
    edgeStroke: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
    ]),
    edgeFill: PropTypes.string,
  }).isRequired,
};

// defaults comuns — cada subclasse espalha e define type + coleção vazia
export const straightLineBaseDefaults = {
  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  currentPositionStroke: "#000000",
  currentPositionstrokeOpacity: 1,
  currentPositionStrokeWidth: 3,
  currentPositionRadius: 0,

  shouldDisableSnap: (e) => e.button === 2 || e.shiftKey,
  // Whether mouse should snap to existing points. Default to false to avoid
  // PropTypes warnings when callers don't provide this prop.
  snap: false,
  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },

  appearance: {
    stroke: {
      color: "#000000",
      width: 1,
      opacity: 1,
      dash: "Solid",
    },
    edgeStroke: { color: "#000000", dash: "Solid", width: 1, opacity: 1 },
    edgeFill: "#FFFFFF",
    r: 6,
  },
};

StraightLineTool.propTypes = {
  ...straightLinePropTypes,
  type: PropTypes.string,
};

export default StraightLineTool;
